import { apiBase as BACKEND_URL, portalUrl } from '../environment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TagInput from './TagInput';

const createContact = (primary = false) => ({
  id: `contact_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  email: '',
  phone: '',
  isAccountAdmin: primary,
  receivesNotifications: true,
  canCreateJobs: true,
});

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizePhone = (value) => {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (raw.startsWith('+')) return `+${digits}`;
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

const unique = (values) => Array.from(new Set(values.filter(Boolean)));
const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));
export const normalizeAddressUnitLabel = (value) => {
  const raw = String(value || '').trim().replace(/^[,\s]+|[,\s]+$/g, '');
  if (!raw) return '';
  const match = raw.match(/^(suite|ste|unit|apartment|apt|#)\s*[-:#.]?\s*(.*)$/i);
  if (!match) return raw;
  const label = /^(suite|ste)$/i.test(match[1])
    ? 'Suite'
    : /^(apartment|apt)$/i.test(match[1])
      ? 'Apt'
      : 'Unit';
  return `${label} ${String(match[2] || '').trim()}`.trim();
};

export const addressWithUnit = (address, unit) => {
  const base = String(address || '').trim();
  const secondary = normalizeAddressUnitLabel(unit);
  return secondary ? `${base}, ${secondary}` : base;
};

const createAddressSessionToken = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `signup_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

export function AddressInput({ value, onChange, onResolved, onError }) {
  const inputRef = useRef(null);
  const selectedValueRef = useRef('');
  const sessionTokenRef = useRef(createAddressSessionToken());
  const abortRef = useRef(null);
  const [resolving, setResolving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  useEffect(() => {
    const query = String(value || '').trim();
    if (query === selectedValueRef.current || query.length < 3) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsLoading(false);
      abortRef.current?.abort();
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSuggestionsLoading(true);
      try {
        const params = new URLSearchParams({
          input: query,
          sessionToken: sessionTokenRef.current,
        });
        const response = await fetch(`${BACKEND_URL}/api/public/address-suggestions?${params}`, {
          signal: controller.signal,
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body?.ok !== true) {
          throw new Error(body?.error || 'Address suggestions are temporarily unavailable.');
        }
        const next = Array.isArray(body?.suggestions) ? body.suggestions : [];
        setSuggestions(next);
        setSuggestionsOpen(next.length > 0);
        setActiveSuggestion(-1);
        onError(next.length ? '' : 'No exact Google matches yet. Add the city, state, or ZIP code.');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setSuggestions([]);
        setSuggestionsOpen(false);
        onError('Google suggestions are temporarily unavailable. Enter the full address and choose Confirm full address.');
      } finally {
        if (abortRef.current === controller) setSuggestionsLoading(false);
      }
    }, 275);

    return () => {
      window.clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [onError, value]);

  const resolveAddress = async (rawAddress, preferredPlaceId = '') => {
    const normalizedAddress = String(rawAddress || '').trim();
    if (!normalizedAddress) {
      onError('Enter the complete business address first.');
      return;
    }
    setResolving(true);
    setSuggestionsOpen(false);
    onError('');
    try {
      const response = await fetch(`${BACKEND_URL}/resolve-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rawAddress: normalizedAddress,
          ...(preferredPlaceId ? {
            placeId: preferredPlaceId,
            sessionToken: sessionTokenRef.current,
          } : {}),
        }),
      });
      const body = await response.json().catch(() => ({}));
      const formattedAddress = String(body?.formatted_address || '').trim();
      const placeId = String(preferredPlaceId || body?.place_id || '').trim();
      const lat = Number(body?.lat);
      const lng = Number(body?.lng);
      if (!response.ok || !formattedAddress || !placeId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error(body?.error || 'We could not confirm that address. Add more detail and try again.');
      }
      selectedValueRef.current = formattedAddress;
      onChange(formattedAddress, false);
      onResolved({ standardizedAddress: formattedAddress, placeId, lat, lng });
      onError('');
      setSuggestions([]);
      sessionTokenRef.current = createAddressSessionToken();
    } catch (error) {
      onError(String(error?.message || error));
    } finally {
      setResolving(false);
    }
  };

  const chooseSuggestion = (suggestion) => {
    if (!suggestion?.description) return;
    selectedValueRef.current = String(suggestion.description).trim();
    onChange(selectedValueRef.current, false);
    resolveAddress(suggestion.description, suggestion.placeId);
  };

  const resolveTypedAddress = async () => {
    resolveAddress(String(value || '').trim());
  };

  const handleKeyDown = (event) => {
    if (!suggestionsOpen || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      chooseSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === 'Escape') {
      setSuggestionsOpen(false);
    }
  };

  return (
    <div className="address-search">
      <div className="address-control">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="google-address-suggestions"
          aria-expanded={suggestionsOpen}
          autoComplete="off"
          placeholder="Start typing the street address…"
          value={value}
          onFocus={() => setSuggestionsOpen(suggestions.length > 0)}
          onBlur={() => window.setTimeout(() => setSuggestionsOpen(false), 120)}
          onKeyDown={handleKeyDown}
          onChange={(event) => {
            selectedValueRef.current = '';
            onChange(event.target.value, true);
          }}
        />
        <button type="button" disabled={resolving} onClick={resolveTypedAddress}>
          {resolving ? 'Checking…' : 'Confirm full address'}
        </button>
      </div>
      {suggestionsLoading ? <span className="address-searching">Searching Google addresses…</span> : null}
      {suggestionsOpen && suggestions.length ? (
        <div className="address-suggestions" id="google-address-suggestions" role="listbox">
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === activeSuggestion}
              className={index === activeSuggestion ? 'active' : ''}
              key={suggestion.placeId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseSuggestion(suggestion)}
            >
              <strong>{suggestion.primaryText || suggestion.description}</strong>
              {suggestion.secondaryText ? <small>{suggestion.secondaryText}</small> : null}
            </button>
          ))}
          <div className="google-attribution" aria-label="Powered by Google">Powered by Google</div>
        </div>
      ) : null}
    </div>
  );
}

function OnboardingForm() {
  const notifyRef = useRef(null);
  const [form, setForm] = useState({
    companyName: '',
    locationIdentifier: '',
    rawAddress: '',
    addressUnit: '',
    contacts: [createContact(true)],
    distributionListName: '',
    additionalNotifyEmails: [],
    allowDomainWideJobCreation: false,
    agreedToTerms: false,
    smsOptIn: false,
  });
  const [addressMeta, setAddressMeta] = useState(null);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value, ...(key === 'contacts' ? { smsOptIn:false } : {}) }));
  const updateContact = (id, key, value) => {
    setForm((current) => ({
      ...current,
      ...(current.contacts[0]?.id === id && ['phone','email','name'].includes(key) ? { smsOptIn:false } : {}),
      contacts: current.contacts.map((contact) => (
        contact.id === id ? { ...contact, [key]: value } : contact
      )),
    }));
  };

  const errors = useMemo(() => {
    const next = [];
    if (!form.companyName.trim()) next.push('Enter the legal business name.');
    if (!form.locationIdentifier.trim()) next.push('Add a city, branch, or site code for this location.');
    if (!form.rawAddress.trim()) next.push('Enter the business address.');
    if (!addressMeta?.placeId || !Number.isFinite(addressMeta?.lat) || !Number.isFinite(addressMeta?.lng)) {
      next.push('Select the business address from Google suggestions.');
    }
    if (addressMeta?.placeId && !addressConfirmed) {
      next.push('Confirm the complete Google address, including the suite or unit if applicable.');
    }
    const completeContacts = form.contacts.filter((contact) => (
      contact.name.trim() && validEmail(contact.email) && normalizePhone(contact.phone)
    ));
    if (!completeContacts.length) next.push('Add at least one complete contact.');
    form.contacts.forEach((contact, index) => {
      if (!contact.name.trim()) next.push(`Enter a name for contact ${index + 1}.`);
      if (!validEmail(contact.email)) next.push(`Enter a valid work email for contact ${index + 1}.`);
      if (!normalizePhone(contact.phone)) next.push(`Enter a mobile number for contact ${index + 1}.`);
    });
    if (!completeContacts.some((contact) => contact.isAccountAdmin)) {
      next.push('Choose at least one account and billing administrator.');
    }
    const invalidExtraEmail = form.additionalNotifyEmails.find((email) => !validEmail(email));
    if (invalidExtraEmail) next.push(`Check the additional email: ${invalidExtraEmail}`);
    if (form.additionalNotifyEmails.length && !form.distributionListName.trim()) {
      next.push('Name the notification distribution list.');
    }
    if (form.distributionListName.trim() && !form.additionalNotifyEmails.length) {
      next.push('Add at least one recipient to the named distribution list.');
    }
    if (!form.agreedToTerms) next.push('Accept the Terms of Service to continue.');
    return unique(next);
  }, [addressConfirmed, addressMeta, form]);

  const buildPayload = () => {
    const contacts = form.contacts.map((contact) => ({
      ...contact,
      name: contact.name.trim(),
      email: normalizeEmail(contact.email),
      phone: normalizePhone(contact.phone),
    }));
    const admins = contacts.filter((contact) => contact.isAccountAdmin);
    const notifyEmails = unique([
      ...contacts.filter((contact) => contact.receivesNotifications).map((contact) => contact.email),
      ...form.additionalNotifyEmails.map(normalizeEmail),
    ]);
    const companyName = form.companyName.trim();
    const locationIdentifier = form.locationIdentifier.trim();
    return {
      companyName,
      locationIdentifier,
      contacts,
      distributionListName: form.distributionListName.trim(),
      additionalNotifyEmails: form.additionalNotifyEmails.map(normalizeEmail),
      requestedDistributionLists: form.distributionListName.trim() && form.additionalNotifyEmails.length
        ? [{
          name: form.distributionListName.trim(),
          description: 'Created during client onboarding.',
          visibility: 'shared',
          recipients: form.additionalNotifyEmails.map(normalizeEmail),
        }]
        : [],
      rawAddress: addressWithUnit(form.rawAddress, form.addressUnit),
      baseAddress: form.rawAddress.trim(),
      addressUnit: normalizeAddressUnitLabel(form.addressUnit),
      agreedToTerms: true,
      marketingOptIn: false,
      name: [`${companyName} — ${locationIdentifier}`],
      email: unique(contacts.map((contact) => contact.email)),
      phone: unique(contacts.map((contact) => contact.phone)),
      billingContact: unique(admins.map((contact) => contact.name)),
      billingEmail: unique(admins.map((contact) => contact.email)),
      billingPhone: unique(admins.map((contact) => contact.phone)),
      notifyEmails,
      notifyPhones: [], // Email notifications never opt a number into SMS.
      smsConsent: { optIn: form.smsOptIn === true, version: 'chemcab_courier_sms_v1_20261022', phoneNumber: contacts[0]?.phone || null },
      jobCreatorEmails: unique(
        contacts.filter((contact) => contact.canCreateJobs).map((contact) => contact.email)
      ),
      addedAdminEmails: [],
      telecomContacts: contacts.map(({ name, email, phone }) => ({ name, email, phone })),
      allowDomainWideJobCreation: form.allowDomainWideJobCreation === true,
    };
  };

  const submit = async (event) => {
    event.preventDefault();
    notifyRef.current?.commit();
    setAttemptedSubmit(true);
    setSubmitError('');
    if (errors.length) return;
    setBusy(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/process-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData: buildPayload(),
          standardizedAddress: addressWithUnit(addressMeta.standardizedAddress, form.addressUnit),
          baseStandardizedAddress: addressMeta.standardizedAddress,
          addressUnit: normalizeAddressUnitLabel(form.addressUnit),
          placeId: addressMeta.placeId,
          lat: addressMeta.lat,
          lng: addressMeta.lng,
          isTrusted: false,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.success !== true) {
        throw new Error(body?.error || 'We could not submit the request. Please try again.');
      }
      setReceipt(body);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitError(String(error?.message || error));
    } finally {
      setBusy(false);
    }
  };

  if (receipt) {
    return (
      <div className="signup-page">
        <header className="brand-bar"><a href="https://www.chemcab.com/">CHEMCAB</a><a className="portal-link" href={portalUrl}>Client Portal</a></header>
        <main className="success-wrap">
          <section className="signup-card success-card">
            <span className="success-icon">✓</span>
            <p className="eyebrow">Request received</p>
            <h1>We have what we need.</h1>
            <p>Our team will review the business and location details. The primary account contacts will receive an email when the account is approved.</p>
            {receipt.status === 'existing_client' ? <div className="notice">An account already exists for this address. Sign in or contact ChemCab to be added.</div> : null}
            <a className="primary-button" href={portalUrl}>Go to Client Portal</a>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <header className="brand-bar">
        <a href="https://www.chemcab.com/">CHEMCAB</a>
        <a className="portal-link" href={portalUrl}>Already a client? Sign in</a>
      </header>
      <main className="signup-layout">
        <aside className="signup-intro">
          <p className="eyebrow">Client onboarding</p>
          <h1>Specialized courier service, built around your operation.</h1>
          <p>Tell us who should manage your account and where couriers will normally pick up. Most teams finish in under five minutes.</p>
          <ol>
            <li><span>1</span><div><strong>Name your location</strong><small>Company name plus a city, branch, or site code.</small></div></li>
            <li><span>2</span><div><strong>ChemCab reviews it</strong><small>We confirm service fit and account details.</small></div></li>
            <li><span>3</span><div><strong>Sign in and request jobs</strong><small>Approved contacts receive the portal link.</small></div></li>
          </ol>
        </aside>

        <form className="signup-card onboarding-form" onSubmit={submit} noValidate>
          <div className="form-heading">
            <p className="eyebrow">Account request</p>
            <h2>Business and location</h2>
            <p>Enter the legal company name, then add a city, branch, or site code that distinguishes this pickup location.</p>
          </div>
          <div className="form-grid">
            <label className="field"><span>Legal company name *</span><input autoComplete="organization" value={form.companyName} onChange={(event) => update('companyName', event.target.value)} placeholder="Example Company LLC" /></label>
            <label className="field"><span>Location identifier *</span><input value={form.locationIdentifier} onChange={(event) => update('locationIdentifier', event.target.value)} placeholder="North Campus, Lab B, or LOC-01" /><small>Portal name: {form.companyName.trim() || 'Company'} — {form.locationIdentifier.trim() || 'Location'}</small></label>
            <div className="address-requirement field-wide"><strong>Google-confirmed location required</strong><span>Start typing, then choose the correct suggestion. This prevents duplicate or unreachable client locations.</span></div>
            <label className="field field-wide"><span>Primary pickup address *</span><AddressInput value={form.rawAddress} onChange={(value, typed = false) => { update('rawAddress', value); setAddressConfirmed(false); if (typed) setAddressMeta(null); }} onResolved={(meta) => { setAddressMeta(meta); setAddressConfirmed(false); }} onError={setAddressError} /><small className={addressMeta ? 'address-ok' : ''}>{addressMeta ? 'Google location found. Review the complete address below.' : addressError || 'Choose one of the Google address suggestions to continue.'}</small></label>
            <label className="field field-wide"><span>Suite / unit / apartment <span className="optional-label">Optional</span></span><input autoComplete="address-line2" value={form.addressUnit} onChange={(event) => { update('addressUnit', event.target.value); setAddressConfirmed(false); }} maxLength={60} placeholder="Suite 240, Unit B, or Apt 3B" /><small>Use a label such as Suite, Unit, or Apt. It becomes part of this location’s unique verified address.</small></label>
            {addressMeta ? (
              <section className="address-confirmation field-wide" aria-live="polite">
                <div><span>Google-confirmed business location</span><strong>{addressWithUnit(addressMeta.standardizedAddress, form.addressUnit)}</strong></div>
                <label><input type="checkbox" checked={addressConfirmed} onChange={(event) => setAddressConfirmed(event.target.checked)} /><span>Yes, this complete address{form.addressUnit.trim() ? ', including the suite or unit,' : ''} is correct. *</span></label>
              </section>
            ) : null}
          </div>

          <div className="section-divider" />
          <div className="form-heading compact"><h2>People and permissions</h2><p>Add the primary contacts for this location. At least one person must manage account and billing details.</p></div>
          <div className="contact-list">
            {form.contacts.map((contact, index) => (
              <section className="contact-card" key={contact.id}>
                <div className="contact-title"><div><span>Contact {index + 1}</span><strong>{index === 0 ? 'Primary contact' : 'Additional contact'}</strong></div>{form.contacts.length > 1 ? <button type="button" onClick={() => update('contacts', form.contacts.filter((item) => item.id !== contact.id))}>Remove</button> : null}</div>
                <div className="form-grid three-column">
                  <label className="field"><span>Full name *</span><input autoComplete="name" value={contact.name} onChange={(event) => updateContact(contact.id, 'name', event.target.value)} placeholder="Primary contact name" /></label>
                  <label className="field"><span>Work email *</span><input type="email" autoComplete="email" value={contact.email} onChange={(event) => updateContact(contact.id, 'email', event.target.value)} placeholder="name@example.com" /></label>
                  <label className="field"><span>Mobile number *</span><input type="tel" autoComplete="tel" value={contact.phone} onChange={(event) => updateContact(contact.id, 'phone', event.target.value)} placeholder="(555) 000-0000" /></label>
                </div>
                <div className="permission-grid">
                  <label><input type="checkbox" checked={contact.isAccountAdmin} onChange={(event) => updateContact(contact.id, 'isAccountAdmin', event.target.checked)} /><span><strong>Account + billing admin</strong><small>Manage users, access, and invoices.</small></span></label>
                  <label><input type="checkbox" checked={contact.canCreateJobs} onChange={(event) => updateContact(contact.id, 'canCreateJobs', event.target.checked)} /><span><strong>Can request couriers</strong><small>Create and manage job requests.</small></span></label>
                  <label><input type="checkbox" checked={contact.receivesNotifications} onChange={(event) => updateContact(contact.id, 'receivesNotifications', event.target.checked)} /><span><strong>Receives job updates</strong><small>Get confirmation and status emails.</small></span></label>
                </div>
              </section>
            ))}
          </div>
          <div className="contact-actions">
            <button className="secondary-button" type="button" onClick={() => update('contacts', [...form.contacts, createContact(false)])}>+ Add another contact</button>
            <p>Additional users, roles, and notifications can be configured later in the Client Portal.</p>
          </div>

          <div className="organization-permission">
            <label>
              <input type="checkbox" checked={form.allowDomainWideJobCreation} onChange={(event) => update('allowDomainWideJobCreation', event.target.checked)} />
              <span><strong>Allow company-wide courier requests</strong><small>Anyone with a verified email at your company domain can request couriers for this location.</small></span>
            </label>
          </div>

          <div className="optional-panel">
            <div className="form-heading compact">
              <h2>Notification distribution list <span className="optional-label">Optional</span></h2>
              <p>Name a reusable group for job updates. You can add more lists later in the Client Portal.</p>
            </div>
            <label className="field field-wide distribution-list-name"><span>List name</span><input value={form.distributionListName} onChange={(event) => update('distributionListName', event.target.value)} maxLength={60} placeholder="Operations updates" /></label>
            <TagInput ref={notifyRef} label="Recipients" helperText="Enter each email, then press Enter. This list will be shared with approved users at this location." tags={form.additionalNotifyEmails} setTags={(tags) => update('additionalNotifyEmails', tags)} placeholder="operations@example.com" />
          </div>

          <section className="optional-panel" aria-label="Optional SMS messages">
            <h2>Text messages <span className="optional-label">Optional</span></h2>
            <p>This choice applies only to your primary contact mobile number. Adding other contacts does not enroll them in SMS. Each person must choose for themselves and verify their number in the Portal.</p>
            <label className="terms-row"><input type="checkbox" checked={form.smsOptIn} onChange={event=>update('smsOptIn',event.target.checked)} /><span>I agree to receive ChemCab text messages about courier requests, pickups, delivery updates and support at my mobile number. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. SMS is optional. <a href="https://www.chemcab.com/terms-of-service" target="_blank" rel="noreferrer">SMS Terms</a> · <a href="https://www.chemcab.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a></span></label>
            <small>This records your preference; texts are not activated until your number is verified and SMS service is available.</small>
          </section>

          <label className="terms-row"><input type="checkbox" checked={form.agreedToTerms} onChange={(event) => update('agreedToTerms', event.target.checked)} /><span>I am authorized to submit this request and agree to the <a href="https://www.chemcab.com/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>. *</span></label>

          {attemptedSubmit && errors.length ? <div className="error-panel" role="alert"><strong>Please check the form:</strong><ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul></div> : null}
          {submitError ? <div className="error-panel" role="alert">{submitError}</div> : null}
          <button className="primary-button submit-button" disabled={busy}>{busy ? 'Submitting securely…' : 'Submit account request'}</button>
          <p className="privacy-note">Your information is used only to review and configure your ChemCab account.</p>
        </form>
      </main>
    </div>
  );
}

export default OnboardingForm;
